import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../../utils/axios";
import { 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Td, 
  Box, 
  Button, 
  Spinner, 
  useToast, 
  Heading, 
  Flex,
  Image,
  Input,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  Text,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon
} from "@chakra-ui/react";

const EditProductAddons = ({ productId, productDescription, onBack }) => {
  const [addons, setAddons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [productImage, setProductImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const toast = useToast();

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([fetchAddons(), fetchProductImage()]);
    };

    fetchData();
  }, [productId, toast]);

  const fetchAddons = async () => {
    try {
      const response = await axiosInstance.post(`/dummy/get-addons`, { hproduct_id: productId });
      setAddons(response.data.data);
      toast({
        title: response.data.message,
        description: '',
        status: response.data.status,
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch addons",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductImage = async () => {
    try {
      const response = await axiosInstance.post(`/dummy/get-product-image`, { hproduct_id: productId });
      console.log('fetchProductImage response:', response.data);
      
      if (response.data.status === "success" && response.data.data && response.data.data.length > 0) {
        setProductImage(response.data.data[0].wp_product_image1);
        console.log('Image updated to:', response.data.data[0].wp_product_image1);
      } else {
        console.log('No image found or invalid response');
        setProductImage(null);
      }
    } catch (error) {
      console.error("Failed to fetch product image:", error);
      setProductImage(null);
    } finally {
      setImageLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid file type",
          description: "Please select an image file",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select an image smaller than 5MB",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select an image file first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('hproduct_id', productId);

      const response = await axiosInstance.post('/dummy/update-product-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Debug lines to see what the API returns
      console.log('update-product-image response:', response);
      console.log('update-product-image response.data:', response.data);
      console.log('update-product-image success field:', response.data.success);
      console.log('update-product-image status field:', response.data.status);

      if (response.data.success === true) {
        toast({
          title: "Image updated successfully",
          description: response.data.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Refresh the product image with loading state
        setImageLoading(true);
        await fetchProductImage();
        
        // Clear the selected file
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        toast({
          title: "Error updating image",
          description: response.data.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload image",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploadingImage(false);
    }
  };

  if (isLoading) {
    return <Spinner size="lg" />;
  }

  return (
    <Box>
      <Flex justify="space-between" flexDirection={"row"} gap={4} mb={6}>
        <Heading size="lg">{productDescription}</Heading>
        <Button onClick={onBack} mb={4}>
          Back
        </Button>
      </Flex>

      {/* Product Image Section */}
      <Box mb={6} p={4} border="1px" borderColor="gray.200" borderRadius="md">
        <Heading size="md" mb={4}>Product Image</Heading>
        
        <VStack spacing={4} align="stretch">
          {/* Current Image Display */}
          <Box>
            <Text fontWeight="semibold" mb={2}>Current Image:</Text>
            {imageLoading ? (
              <Spinner size="sm" />
            ) : productImage ? (
              <Image
                src={productImage}
                alt={productDescription}
                maxWidth="300px"
                maxHeight="300px"
                objectFit="contain"
                border="1px"
                borderColor="gray.300"
                borderRadius="md"
              />
            ) : (
              <Box
                width="300px"
                height="200px"
                bg="gray.100"
                display="flex"
                alignItems="center"
                justifyContent="center"
                border="1px"
                borderColor="gray.300"
                borderRadius="md"
              >
                <Text color="gray.500">No image available</Text>
              </Box>
            )}
          </Box>

          {/* Upload Form */}
          <Box>
            <Text fontWeight="semibold" mb={2}>Upload New Image:</Text>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel>Select Image File</FormLabel>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  p={1}
                />
              </FormControl>

              {selectedFile && (
                <Text fontSize="sm" color="gray.600">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </Text>
              )}

              <HStack spacing={2}>
                <Button
                  colorScheme="blue"
                  onClick={handleImageUpload}
                  isLoading={uploadingImage}
                  loadingText="Uploading..."
                  isDisabled={!selectedFile}
                  size="sm"
                >
                  Upload Image
                </Button>
                
                {selectedFile && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    size="sm"
                  >
                    Clear Selection
                  </Button>
                )}
              </HStack>
            </VStack>
          </Box>
        </VStack>
      </Box>

      <Divider mb={6} />

      {/* Addons Table */}
      <Accordion allowToggle>
        <AccordionItem>
          <AccordionButton>
            <Box flex="1" textAlign="left">
              <Heading size="md">Product Add-ons</Heading>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={4}>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>ID</Th>
                  <Th>Type</Th>
                  <Th>Grouping</Th>
                  <Th>Group Order</Th>
                  <Th>Selection Type</Th>
                  <Th>Selection Display</Th>
                  <Th>Group Title Display</Th>
                  <Th>Option Display</Th>
                  <Th>Charge</Th>
                </Tr>
              </Thead>
              <Tbody>
                {addons.map((addon) => (
                  <Tr key={addon.id}>
                    <Td>{addon.id}</Td>
                    <Td>{addon.type}</Td>
                    <Td>{addon.grouping}</Td>
                    <Td>{addon.group_order}</Td>
                    <Td>{addon.selection_type}</Td>
                    <Td>{addon.selection_display}</Td>
                    <Td>{addon.group_title_display}</Td>
                    <Td>{addon.option_display}</Td>
                    <Td>{addon.charge}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
};

export default EditProductAddons;
